// apps/roleforge-app/src/lib/use-activity-stream.ts
//
// Activity-event subscription. The router (sse-event-router) already enforces
// min-display-duration, so this hook just collects router-gated events into
// an array and applies the voice-rule guard.

import { useEffect, useState } from 'react';
import type { SseEventRouter } from './sse-event-router';
import type { ActivityEventPayload } from './converse-types';

export interface ActivityItem {
  id: string;
  kind: ActivityEventPayload['kind'];
  message: string;
  icon: ActivityEventPayload['icon'];
  rotatingMessages?: string[];
}

export interface UseActivityStreamOptions {
  auditStatus?: string;
  completionVisibleMs?: number;
  streamEndVisibleMs?: number;
}

const FORBIDDEN: RegExp[] = [
  /\bupsert_\w+/,
  /\brecord_pain_point\b/,
  /\bsearch_skills_catalog\b/,
  /\badd_opportunity\b/,
  /\bscore_opportunity\b/,
  /\bassign_role\b/,
  /\bnarrate_action\b/,
  /\bengine_(created|step_added)\b/,
  /\bsynthesis_assembled\b/,
  /\{[^}]*"[^"]+"\s*:/,
];

export function isVoiceCompliant(message: string): boolean {
  for (const re of FORBIDDEN) {
    if (re.test(message)) return false;
  }
  return true;
}

let counter = 0;
function nextId(): string {
  counter += 1;
  return `activity-${counter}-${Date.now()}`;
}

function statusTone(status?: string): 'discovery' | 'map' | 'findings' | 'activation' {
  if (status === 'synthesizing') return 'map';
  if (status === 'validating') return 'findings';
  if (status === 'ready_to_materialize' || status === 'materialized') return 'activation';
  return 'discovery';
}

function rotatingMessagesFor(payload: ActivityEventPayload, auditStatus?: string): string[] | undefined {
  if (payload.kind !== 'activity.started') return undefined;

  const msg = payload.message.toLowerCase();
  if (msg.includes('audit corpus') || msg.includes('similar')) {
    return [
      'Comparing this against similar business patterns...',
      'Checking what worked for teams shaped like yours...',
      'Looking for repeatable signals in businesses like yours...',
      'Turning that answer into the next useful question...',
    ];
  }

  if (msg.includes('skill') || msg.includes('catalog') || msg.includes('platform')) {
    return [
      'Matching this bottleneck to deployable AI roles...',
      'Checking which skills can own this workflow...',
      'Mapping the work to the right agent shape...',
      'Looking for the smallest useful automation path...',
    ];
  }

  switch (statusTone(auditStatus)) {
    case 'map':
      return [
        'Organizing your answers into a working map...',
        'Finding the handoffs and repeatable steps...',
        'Separating one-off work from system work...',
      ];
    case 'findings':
      return [
        'Scoring the strongest opportunities...',
        'Checking the effort against likely savings...',
        'Sorting the ideas into practical next moves...',
      ];
    case 'activation':
      return [
        'Preparing the build-ready handoff...',
        'Checking the recommended team shape...',
        'Packaging the pieces you can keep using...',
      ];
    default:
      return [
        'Reading your answer for operating patterns...',
        'Looking for the next useful discovery thread...',
        'Checking similar business signals...',
      ];
  }
}

function displayMessageFor(payload: ActivityEventPayload, auditStatus?: string): Pick<ActivityItem, 'message' | 'rotatingMessages'> {
  const rotatingMessages = rotatingMessagesFor(payload, auditStatus);
  if (rotatingMessages && rotatingMessages.length > 0) {
    return { message: rotatingMessages[0], rotatingMessages };
  }
  return { message: payload.message };
}

export function useActivityStream(
  router: SseEventRouter,
  options: UseActivityStreamOptions = {},
): ActivityItem[] {
  const {
    auditStatus,
    completionVisibleMs = 3600,
    streamEndVisibleMs = 1200,
  } = options;
  const [items, setItems] = useState<ActivityItem[]>([]);

  useEffect(() => {
    const timers = new Set<ReturnType<typeof setTimeout>>();

    const scheduleRemove = (id: string, delay: number) => {
      const timer = setTimeout(() => {
        timers.delete(timer);
        setItems((prev) => prev.filter((item) => item.id !== id));
      }, delay);
      timers.add(timer);
    };

    const append = (payload: ActivityEventPayload) => {
      if (!isVoiceCompliant(payload.message)) return;
      const id = nextId();
      const display = displayMessageFor(payload, auditStatus);
      const item: ActivityItem = {
        id,
        kind: payload.kind,
        message: display.message,
        icon: payload.kind === 'activity.started' ? 'spinner' : payload.icon,
        rotatingMessages: display.rotatingMessages,
      };

      setItems((prev) => {
        const withoutActiveStarted = prev.filter((existing) => existing.kind !== 'activity.started');
        return [...withoutActiveStarted, item];
      });

      if (payload.kind !== 'activity.started') {
        scheduleRemove(id, completionVisibleMs);
      }
    };

    const u1 = router.subscribe('activity', 'activity.started', (ev) => append(ev.payload));
    const u2 = router.subscribe('activity', 'activity.completed', (ev) => append(ev.payload));
    // Failed activity is intentionally not customer-facing; generic stream
    // errors already surface through the chat error path when needed.
    const u4 = router.subscribeChat((ev) => {
      if (ev.type !== 'done' && ev.type !== 'stream_end' && ev.type !== 'error') return;
      const timer = setTimeout(() => {
        timers.delete(timer);
        setItems((prev) => prev.filter((item) => item.kind !== 'activity.started'));
      }, streamEndVisibleMs);
      timers.add(timer);
    });

    return () => {
      for (const timer of timers) clearTimeout(timer);
      timers.clear();
      u1();
      u2();
      u4();
    };
  }, [router, auditStatus, completionVisibleMs, streamEndVisibleMs]);

  return items;
}
